import { useQuery } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope } from "@/api/errors";
import { useCompanyAxis } from "@/api/use-company-context";
import { useKeysetQuery, type KeysetPage } from "@/api/keyset";

import type { paths } from "@/api/schema.generated";

type ReadPayload = paths["/v1/ee/announcements/{id}"]["get"]["responses"][200]["content"]["application/json"]["data"];
export type Announcement = ReadPayload["announcement"];
export type AnnouncementDraft = paths["/v1/ee/announcements"]["post"]["requestBody"]["content"]["application/json"];
export type AnnouncementStatus = Announcement["status"];
export type AnnouncementCompany = paths["/v1/ee/announcements/authority"]["get"]["responses"][200]["content"]["application/json"]["data"]["companies"][number];
export type AnnouncementAction = AnnouncementCompany["actions"][number];
export type AnnouncementAudience = paths["/v1/ee/announcements/audiences"]["get"]["responses"][200]["content"]["application/json"]["data"]["audiences"][number];
export type AudiencePreview = paths["/v1/ee/announcements/audience-preview"]["get"]["responses"][200]["content"]["application/json"]["data"];
export type AnnouncementCommand =
  | { kind: "create"; draft: AnnouncementDraft }
  | { kind: "edit"; id: string; expected_revision: number; draft: AnnouncementDraft }
  | { kind: "publish"; id: string; expected_revision: number; publish_at: string | null; confirmed: true }
  | { kind: "archive"; id: string; expected_revision: number }
  | { kind: "acknowledge"; id: string; confirmed: true };

export async function fetchAnnouncement(id: string, self: boolean): Promise<Announcement> {
  return (await unwrapEnvelope(platformGraph.GET(self ? "/v1/me/announcements/{id}" : "/v1/ee/announcements/{id}", { params: { path: { id } } }))).announcement;
}
export async function fetchAnnouncementPage(self: boolean, cursor: string | null): Promise<KeysetPage<Announcement>> {
  const data = await unwrapEnvelope(platformGraph.GET(self ? "/v1/me/announcements" : "/v1/ee/announcements", { params: { query: { limit: 25, ...(cursor ? { cursor } : {}) } } }));
  return { items: data.announcements, nextCursor: data.next_cursor };
}
export async function fetchAnnouncementAuthority(): Promise<AnnouncementCompany[]> {
  return (await unwrapEnvelope(platformGraph.GET("/v1/ee/announcements/authority"))).companies;
}
export async function fetchAnnouncementPreview(companyId: string, audienceId: string): Promise<AudiencePreview> {
  return unwrapEnvelope(platformGraph.GET("/v1/ee/announcements/audience-preview", { params: { query: { legal_entity_id: companyId, audience_supergroup_id: audienceId } } }));
}
export async function fetchAnnouncementAudiences(companyId: string, cursor: string | null): Promise<KeysetPage<AnnouncementAudience>> {
  const data = await unwrapEnvelope(platformGraph.GET("/v1/ee/announcements/audiences", { params: { query: { legal_entity_id: companyId, limit: 100, ...(cursor ? { cursor } : {}) } } }));
  return { items: data.audiences, nextCursor: data.next_cursor };
}
export async function sendAnnouncementCommand(command: AnnouncementCommand, key: string): Promise<Announcement> {
  const headers = { "x-idempotency-key": key };
  if (command.kind === "create") return (await unwrapEnvelope(platformGraph.POST("/v1/ee/announcements", { params: { header: headers }, body: command.draft }))).announcement;
  const params = { path: { id: command.id }, header: headers };
  if (command.kind === "edit") return (await unwrapEnvelope(platformGraph.PATCH("/v1/ee/announcements/{id}", { params, body: { expected_revision: command.expected_revision, draft: command.draft } }))).announcement;
  if (command.kind === "acknowledge") return (await unwrapEnvelope(platformGraph.POST("/v1/ee/announcements/{id}/acknowledge", { params, body: { confirmed: command.confirmed } }))).announcement;
  if (command.kind === "archive") return (await unwrapEnvelope(platformGraph.POST("/v1/ee/announcements/{id}/archive", { params, body: { expected_revision: command.expected_revision } }))).announcement;
  return (await unwrapEnvelope(platformGraph.POST("/v1/ee/announcements/{id}/publish", { params, body: { expected_revision: command.expected_revision, publish_at: command.publish_at, confirmed: command.confirmed } }))).announcement;
}

export function useAnnouncementScope(): readonly string[] {
  const { user } = useAuth();
  const { active_legal_entity_id } = useCompanyAxis();
  return ["announcements", String(user?.profile.iss ?? ""), String(user?.profile.tenant_id ?? ""), user?.profile.sub ?? "", active_legal_entity_id ?? "all"];
}
export function useAnnouncementAuthority(enabled = true) {
  const scope = useAnnouncementScope();
  return useQuery({ queryKey: [...scope, "authority"], queryFn: fetchAnnouncementAuthority, enabled, staleTime: 0 });
}
export function useAnnouncements(self: boolean) {
  const scope = useAnnouncementScope();
  return useKeysetQuery({ queryKey: [...scope, self ? "self" : "admin", "list"], fetchPage: (cursor) => fetchAnnouncementPage(self, cursor) });
}
export function useAnnouncement(id: string | undefined, self: boolean) {
  const scope = useAnnouncementScope();
  return useQuery({ queryKey: [...scope, self ? "self" : "admin", id], queryFn: () => fetchAnnouncement(id!, self), enabled: Boolean(id),
    refetchInterval: (query) => query.state.data?.status === "scheduled" ? 5000 : false });
}
export function hasAnnouncementAction(companies: readonly AnnouncementCompany[] | undefined, companyId: string, action: AnnouncementAction): boolean {
  return companies?.some((company) => company.legal_entity_id === companyId && company.actions.includes(action)) === true;
}
